import { useEffect, useState } from 'react';
import { engagementAPI, productsAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Plus, Search, Mail, Clock, Users, BarChart3, Play, Pause, Trash2,
  Send, MessageSquare, Star, ThumbsUp, Calendar, Target, Zap, Eye
} from 'lucide-react';

const triggerTypes = [
  { value: 'product_purchase', label: 'Product Purchase', icon: '🛒', desc: 'After product assigned to contact' },
  { value: 'ticket_resolved', label: 'Ticket Resolved', icon: '✅', desc: 'After support ticket closed' },
  { value: 'warranty_registered', label: 'Warranty Registered', icon: '📋', desc: 'After warranty registration' },
  { value: 'time_based', label: 'Time Based', icon: '⏰', desc: 'Scheduled at intervals' },
  { value: 'manual', label: 'Manual', icon: '👆', desc: 'Manually triggered' },
];

const emailTypes = [
  { value: 'check_in', label: 'Check-in', icon: '👋', desc: 'How\'s it going?' },
  { value: 'rating_request', label: 'Rating Request', icon: '⭐', desc: 'Ask for 1-5 stars' },
  { value: 'nps_survey', label: 'NPS Survey', icon: '📊', desc: 'Net Promoter Score' },
  { value: 'feedback_form', label: 'Feedback Form', icon: '💬', desc: 'Open feedback' },
  { value: 'thank_you', label: 'Thank You', icon: '🙏', desc: 'Confirmation' },
  { value: 'custom', label: 'Custom', icon: '✏️', desc: 'Custom template' },
];

const statusColors = {
  draft: 'bg-slate-100 text-slate-700',
  active: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-amber-100 text-amber-700',
  completed: 'bg-blue-100 text-blue-700',
  archived: 'bg-slate-100 text-slate-500',
};

export const EngagementCampaignsPage = () => {
  const { hasRole } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_type: 'product_purchase',
    email_type: 'check_in',
    delay_days: 30,
    subject: '',
    body: '',
    target_all_contacts: true,
    target_product_ids: [],
    is_recurring: false,
    recurring_interval_days: 90,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [campaignsRes, statsRes, productsRes] = await Promise.all([
        engagementAPI.getAll(),
        engagementAPI.getStats(),
        productsAPI.getAll()
      ]);
      setCampaigns(campaignsRes.data);
      setStats(statsRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.subject || !formData.body) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await engagementAPI.create(formData);
      toast.success('Campaign created');
      setCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to create campaign');
    }
  };

  const handleActivate = async (campaign) => {
    try {
      const res = await engagementAPI.activate(campaign.id);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to activate campaign');
    }
  };

  const handlePause = async (campaign) => {
    try {
      await engagementAPI.pause(campaign.id);
      toast.success('Campaign paused');
      fetchData();
    } catch (error) {
      toast.error('Failed to pause campaign');
    }
  };

  const handleDelete = async (campaign) => {
    if (!window.confirm('Delete this campaign? This will also delete all scheduled emails.')) return;
    
    try {
      await engagementAPI.delete(campaign.id);
      toast.success('Campaign deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const openCampaignDetail = async (campaign) => {
    setSelectedCampaign(campaign);
    setDetailDialog(true);
    
    try {
      const res = await engagementAPI.getScheduled(campaign.id);
      setScheduledEmails(res.data);
    } catch (error) {
      console.error('Failed to fetch scheduled emails');
    }
  };

  const handleSendNow = async (engagementId) => {
    try {
      const res = await engagementAPI.sendNow(selectedCampaign.id, engagementId);
      toast.success(res.data.message);
      // Refresh scheduled list
      const updated = await engagementAPI.getScheduled(selectedCampaign.id);
      setScheduledEmails(updated.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to send email');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      trigger_type: 'product_purchase',
      email_type: 'check_in',
      delay_days: 30,
      subject: '',
      body: '',
      target_all_contacts: true,
      target_product_ids: [],
      is_recurring: false,
      recurring_interval_days: 90,
    });
  };

  const filteredCampaigns = campaigns.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Template suggestions based on email type
  const getTemplateContent = (emailType, triggerType) => {
    const templates = {
      check_in: {
        subject: "How's everything going, {{first_name}}?",
        body: `Hi {{first_name}},\n\nWe hope you're enjoying your recent {{trigger_item}}! We wanted to check in and see how things are going.\n\nIs there anything we can help you with? We'd love to hear about your experience.\n\nBest regards,\nThe Team`
      },
      rating_request: {
        subject: "Quick question about your experience",
        body: `Hi {{first_name}},\n\nWe hope you've had a chance to use your {{trigger_item}}. We'd really appreciate if you could take a moment to rate your experience.\n\n⭐⭐⭐⭐⭐\n\nYour feedback helps us improve and serve you better!\n\nThank you,\nThe Team`
      },
      nps_survey: {
        subject: "How likely would you recommend us?",
        body: `Hi {{first_name}},\n\nWe value your opinion! On a scale of 0-10, how likely are you to recommend us to a friend or colleague?\n\n0 -------- 5 -------- 10\n\nYour feedback is incredibly important to us.\n\nThank you,\nThe Team`
      },
      feedback_form: {
        subject: "We'd love your feedback!",
        body: `Hi {{first_name}},\n\nYour opinion matters to us. We'd love to hear about your experience with {{trigger_item}}.\n\nWhat's working well?\nWhat could be improved?\n\nPlease reply to this email with your thoughts.\n\nBest,\nThe Team`
      },
      thank_you: {
        subject: "Thank you for your purchase!",
        body: `Hi {{first_name}},\n\nThank you for your recent purchase! We're thrilled to have you as a customer.\n\nIf you have any questions about your {{trigger_item}}, don't hesitate to reach out.\n\nWelcome aboard!\nThe Team`
      }
    };
    
    return templates[emailType] || { subject: '', body: '' };
  };

  const applyTemplate = () => {
    const template = getTemplateContent(formData.email_type, formData.trigger_type);
    setFormData(prev => ({
      ...prev,
      subject: template.subject,
      body: template.body
    }));
    toast.success('Template applied');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="engagement-campaigns-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Engagement Campaigns</h1>
          <p className="text-muted-foreground mt-1">Automated email engagement with timer-based triggers</p>
        </div>
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-campaign-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Engagement Campaign</DialogTitle>
              <DialogDescription>
                Set up automated emails triggered by customer actions
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Campaign Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g., 30-Day Product Follow-up"
                    required
                    data-testid="campaign-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                    placeholder="Describe what this campaign does..."
                    rows={2}
                  />
                </div>
              </div>

              {/* Trigger Settings */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Zap className="h-4 w-4" /> Trigger Settings
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Trigger Type</Label>
                    <Select 
                      value={formData.trigger_type} 
                      onValueChange={(v) => setFormData(p => ({ ...p, trigger_type: v }))}
                    >
                      <SelectTrigger data-testid="trigger-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {triggerTypes.map(t => (
                          <SelectItem key={t.value} value={t.value}>
                            <span className="flex items-center gap-2">
                              <span>{t.icon}</span> {t.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {triggerTypes.find(t => t.value === formData.trigger_type)?.desc}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Delay (Days)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.delay_days}
                      onChange={(e) => setFormData(p => ({ ...p, delay_days: parseInt(e.target.value) || 0 }))}
                      data-testid="delay-days-input"
                    />
                    <p className="text-xs text-muted-foreground">Days after trigger to send email</p>
                  </div>
                </div>

                {formData.trigger_type === 'product_purchase' && (
                  <div className="space-y-2">
                    <Label>Filter by Products (optional)</Label>
                    <Select 
                      value={formData.target_product_ids[0] || 'all'} 
                      onValueChange={(v) => setFormData(p => ({ ...p, target_product_ids: v === 'all' ? [] : [v] }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All products" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Products</SelectItem>
                        {products.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {formData.trigger_type === 'time_based' && (
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={formData.is_recurring}
                        onCheckedChange={(v) => setFormData(p => ({ ...p, is_recurring: v }))}
                      />
                      <Label>Recurring</Label>
                    </div>
                    {formData.is_recurring && (
                      <div className="flex items-center gap-2">
                        <Label>Every</Label>
                        <Input
                          type="number"
                          className="w-20"
                          value={formData.recurring_interval_days}
                          onChange={(e) => setFormData(p => ({ ...p, recurring_interval_days: parseInt(e.target.value) || 90 }))}
                        />
                        <Label>days</Label>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Email Content */}
              <div className="p-4 bg-blue-50 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium flex items-center gap-2">
                    <Mail className="h-4 w-4" /> Email Content
                  </h3>
                  <Button type="button" variant="outline" size="sm" onClick={applyTemplate}>
                    Apply Template
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label>Email Type</Label>
                  <Select 
                    value={formData.email_type} 
                    onValueChange={(v) => setFormData(p => ({ ...p, email_type: v }))}
                  >
                    <SelectTrigger data-testid="email-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {emailTypes.map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          <span className="flex items-center gap-2">
                            <span>{t.icon}</span> {t.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Subject Line *</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData(p => ({ ...p, subject: e.target.value }))}
                    placeholder="How's your new {{product_name}}?"
                    required
                    data-testid="subject-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Use {'{{first_name}}'}, {'{{product_name}}'}, {'{{trigger_item}}'} for personalization
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Email Body *</Label>
                  <Textarea
                    value={formData.body}
                    onChange={(e) => setFormData(p => ({ ...p, body: e.target.value }))}
                    placeholder="Write your email content..."
                    rows={6}
                    required
                    data-testid="body-input"
                  />
                </div>
              </div>

              {/* Targeting */}
              <div className="flex items-center gap-4">
                <Switch
                  checked={formData.target_all_contacts}
                  onCheckedChange={(v) => setFormData(p => ({ ...p, target_all_contacts: v }))}
                />
                <div>
                  <Label>Target All Contacts</Label>
                  <p className="text-xs text-muted-foreground">Send to all contacts when triggered</p>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="create-campaign-submit">
                  Create Campaign
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">{stats.total_campaigns}</p>
                <p className="text-sm text-muted-foreground">Campaigns</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-600">{stats.active_campaigns}</p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-amber-600">{stats.total_pending}</p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-600">{stats.total_sent}</p>
                <p className="text-sm text-muted-foreground">Sent</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-indigo-600">{stats.response_rate}%</p>
                <p className="text-sm text-muted-foreground">Response Rate</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-blue-50">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-700">{stats.nps_score}</p>
                <p className="text-sm text-muted-foreground">NPS Score</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search campaigns..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="campaign-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Campaigns Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead>Email Type</TableHead>
                <TableHead>Delay</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Stats</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCampaigns.length > 0 ? (
                filteredCampaigns.map((campaign) => (
                  <TableRow 
                    key={campaign.id} 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => openCampaignDetail(campaign)}
                    data-testid={`campaign-row-${campaign.id}`}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium">{campaign.name}</p>
                        <p className="text-sm text-muted-foreground truncate max-w-[200px]">{campaign.subject}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span>{triggerTypes.find(t => t.value === campaign.trigger_type)?.icon}</span>
                        <span className="text-sm">{triggerTypes.find(t => t.value === campaign.trigger_type)?.label}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span>{emailTypes.find(t => t.value === campaign.email_type)?.icon}</span>
                        <span className="text-sm">{emailTypes.find(t => t.value === campaign.email_type)?.label}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span>{campaign.delay_days} days</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[campaign.status]}>
                        {campaign.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <span className="text-muted-foreground">Sent:</span> {campaign.total_sent || 0}
                        <span className="mx-1">|</span>
                        <span className="text-muted-foreground">Responded:</span> {campaign.total_responded || 0}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        {campaign.status === 'draft' && (
                          <Button size="sm" variant="outline" onClick={() => handleActivate(campaign)} data-testid={`activate-${campaign.id}`}>
                            <Play className="h-4 w-4 mr-1" /> Activate
                          </Button>
                        )}
                        {campaign.status === 'active' && (
                          <Button size="sm" variant="outline" onClick={() => handlePause(campaign)}>
                            <Pause className="h-4 w-4 mr-1" /> Pause
                          </Button>
                        )}
                        {campaign.status === 'paused' && (
                          <Button size="sm" variant="outline" onClick={() => handleActivate(campaign)}>
                            <Play className="h-4 w-4 mr-1" /> Resume
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(campaign)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                    <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No engagement campaigns yet</p>
                    <Button variant="link" onClick={() => setCreateDialog(true)}>
                      Create your first campaign
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Campaign Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedCampaign && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {emailTypes.find(t => t.value === selectedCampaign.email_type)?.icon}
                  {selectedCampaign.name}
                </DialogTitle>
                <DialogDescription>
                  {selectedCampaign.description || 'No description'}
                </DialogDescription>
              </DialogHeader>

              <Tabs defaultValue="details" className="mt-4">
                <TabsList>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="scheduled">
                    Scheduled ({scheduledEmails.length})
                  </TabsTrigger>
                  <TabsTrigger value="preview">Preview</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Trigger</p>
                      <p className="font-medium flex items-center gap-2">
                        {triggerTypes.find(t => t.value === selectedCampaign.trigger_type)?.icon}
                        {triggerTypes.find(t => t.value === selectedCampaign.trigger_type)?.label}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Delay</p>
                      <p className="font-medium">{selectedCampaign.delay_days} days after trigger</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Status</p>
                      <Badge className={statusColors[selectedCampaign.status]}>
                        {selectedCampaign.status}
                      </Badge>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Target</p>
                      <p className="font-medium">
                        {selectedCampaign.target_all_contacts ? 'All Contacts' : 'Selected Contacts'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <p className="text-2xl font-bold">{selectedCampaign.total_scheduled || 0}</p>
                        <p className="text-sm text-muted-foreground">Scheduled</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <p className="text-2xl font-bold text-emerald-600">{selectedCampaign.total_sent || 0}</p>
                        <p className="text-sm text-muted-foreground">Sent</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4 text-center">
                        <p className="text-2xl font-bold text-blue-600">{selectedCampaign.total_responded || 0}</p>
                        <p className="text-sm text-muted-foreground">Responded</p>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="scheduled" className="mt-4">
                  {scheduledEmails.length > 0 ? (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {scheduledEmails.map(email => (
                        <div key={email.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{email.contact_name || email.contact_email}</p>
                            <p className="text-sm text-muted-foreground">
                              Scheduled: {format(new Date(email.scheduled_for), 'PPp')}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={
                              email.status === 'sent' ? 'bg-emerald-100 text-emerald-700' :
                              email.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }>
                              {email.status}
                            </Badge>
                            {email.status === 'pending' && (
                              <Button size="sm" variant="outline" onClick={() => handleSendNow(email.id)}>
                                <Send className="h-4 w-4 mr-1" /> Send Now
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No scheduled emails yet</p>
                      <p className="text-sm">Activate the campaign to schedule emails</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="preview" className="mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">{selectedCampaign.subject}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                        {selectedCampaign.body}
                      </div>
                    </CardContent>
                  </Card>
                  <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                    ⚠️ Emails are currently SIMULATED. Real sending will be enabled before launch.
                  </p>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EngagementCampaignsPage;
